import { Loader2 } from "lucide-react";
import * as React from "react";

import { ReferencedMemoriesSection } from "./ReferencedMemoriesSection";
import type { MemoryAttribution, MemoryBody } from "./types/memory-types";

interface ReferencedMemoriesSectionAsyncProps {
  memoryAttributions?: ReadonlyArray<MemoryAttribution>;
  conversationId?: string;
  messageId?: string;
  onEditMemory?: (memoryId: string, memoryBody: MemoryBody) => void;
  isV2?: boolean;
  activeMemoryId?: string | null;
  onMemoryClick?: (memoryId: string) => void;
}

const MemoryLoadingSpinner: React.FC = () => (
  <div className="mt-4 pt-3 border-t border-border">
    <div className="flex items-center gap-2 text-xs text-muted-foreground">
      <Loader2 className="w-4 h-4 animate-spin" />
      <span>Loading referenced context...</span>
    </div>
  </div>
);

/**
 * Async wrapper for ReferencedMemoriesSection that defers rendering to improve
 * perceived performance. The memory section will render after a short delay,
 * allowing the main message content to appear immediately.
 */
export const ReferencedMemoriesSectionAsync: React.FC<ReferencedMemoriesSectionAsyncProps> = (props) => {
  const [isReady, setIsReady] = React.useState(false);

  // Don't render anything if there are no memory attributions
  if (!props.memoryAttributions || props.memoryAttributions.length === 0) {
    return null;
  }

  // Defer rendering until after the main content has been painted
  React.useEffect(() => {
    // Use requestIdleCallback if available, otherwise setTimeout
    const scheduleRender = () => {
      if ("requestIdleCallback" in window) {
        window.requestIdleCallback(() => setIsReady(true), { timeout: 500 });
      } else {
        setTimeout(() => setIsReady(true), 100);
      }
    };

    scheduleRender();
  }, []);

  // Show loading spinner while waiting
  if (!isReady) {
    return <MemoryLoadingSpinner />;
  }

  // Render the actual memory section once ready
  // We know memoryAttributions is defined because we checked earlier
  return (
    <ReferencedMemoriesSection
      memoryAttributions={props.memoryAttributions}
      conversationId={props.conversationId}
      messageId={props.messageId}
      onEditMemory={props.onEditMemory}
      isV2={props.isV2}
      activeMemoryId={props.activeMemoryId}
      onMemoryClick={props.onMemoryClick}
    />
  );
};
